import type {
  MetricsType,
  JourneyExternalClickFiltersOptions,
  Filters,
  SelectedFilters,
  SavedDashboardFiltersListData,
  Entity,
  FilterByID,
  SavedDashboardFiltersList,
  SelectedFilterColumnsData,
  CurrentJourneyBreadCrumbData,
} from 'src/types/analytics-genesis';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import type {AnalyticsJourneysExternalClickInsightsActions} from 'src/action-creators/analytics/analytics-journey-click-insights';
import {
  RECEIVE_FILTERS_OPTIONS,
  SET_CURRENT_DASHBOARD,
  SET_CURRENT_DASHBOARD_DRILLDOWN,
  RECEIVE_SAVED_DASHBOARDS,
  SET_SELECTED_FILTERS,
  SET_SELECTED_ENTITIES,
  SET_SELECTED_JOURNEYS,
  SET_SELECTED_DATE_RANGE_AND_FREQUENCY,
  SET_SELECTED_METRICS_TYPE,
  SET_CURRENT_JOURNEY_DATA,
  SET_CHART_FREQUENCY,
  RECEIVE_JOURNEY_OPTIONS,
  RECEIVE_JOURNEY_CLICK_INSIGHTS_TABLE_COLUMNS,
  RECEIVE_TOUCHPOINT_TABLE_COLUMNS,
  RECEIVE_PEOPLE_TABLE_COLUMNS,
  CLEAR_SELECTED_FILTERS,
  SET_NAVIGATION_DOWN,
  SET_NAVIGATION_UP,
  SET_SELECTED_DATES,
  SET_LOADING_DASHBOARD_FILTERS,
} from 'src/action-creators/analytics/analytics-journey-click-insights';


export type AnalyticsJourneysExternalClickInsightsState = {
  filtersOptions: JourneyExternalClickFiltersOptions;
  selectedDateRange: DateRangeWithTimezone;
  selectedDates: {
    [key: string]: DateRangeWithTimezone;
  };
  selectedFilters: {
    [key: string]: SelectedFilters;
  };
  selectedMetricsType: MetricsType;
  selectedEntities: {
    [key: string]: Array<Entity>;
  };
  selectedJourneys: {
    [key: string]: Array<FilterByID>;
  };
  saveDashboardFilterList: {
    [key: string]: SavedDashboardFiltersListData;
  };
  currentDashboard: {
    [key: string]: SavedDashboardFiltersList | null;
  };
  currentJourneyData: CurrentJourneyBreadCrumbData | null;
  chartFrequency: string;
  journeysClickInsightsColumnFilter: SelectedFilterColumnsData | null;
  touchpointColumnFilter: SelectedFilterColumnsData | null;
  peopleColumnFilter: SelectedFilterColumnsData | null;
  isDashboardFiltersLoading: boolean;
  isNavigationDown: boolean | null;
  isNavigationUp: boolean | null;
};

const initialState: AnalyticsJourneysExternalClickInsightsState = {
  selectedFilters: {},
  selectedDates: {},
  selectedEntities: {},
  selectedJourneys: {},
  selectedDateRange: {
    startDateUTC: null,
    startDate: null,
    endDateUTC: null,
    endDate: null,
  },
  selectedMetricsType: 'overview',
  currentJourneyData: null,
  filtersOptions: {
    journey_lifecycle_goal: [],
    journey_business_goal: [],
    journey_category: [],
    journey_status: [],
    recipient_type: [],
    journey_name: [],
    event_name: [],
    event_type: [],
  },
  chartFrequency: 'weekly',
  journeysClickInsightsColumnFilter: null,
  touchpointColumnFilter: null,
  peopleColumnFilter: null,
  saveDashboardFilterList: {},
  currentDashboard: {},
  isDashboardFiltersLoading: true,
  isNavigationDown: null,
  isNavigationUp: null,
};

export default (
  state: AnalyticsJourneysExternalClickInsightsState = initialState,
  action: AnalyticsJourneysExternalClickInsightsActions,
): AnalyticsJourneysExternalClickInsightsState => {
  switch (action.type) {
    case SET_SELECTED_FILTERS: {
      const {dashboardKey, filters} = action.payload;
      return {
        ...state,
        selectedFilters: {
          ...state.selectedFilters,
          [dashboardKey]: filters,
        },
      };
    }
    case SET_SELECTED_ENTITIES: {
      const {dashboardKey, entities} = action.payload;
      return {
        ...state,
        selectedEntities: {
          ...state.selectedEntities,
          [dashboardKey]: entities,
        },
      };
    }
    case SET_SELECTED_JOURNEYS: {
      const {dashboardKey, journeys} = action.payload;
      return {
        ...state,
        selectedJourneys: {
          ...state.selectedJourneys,
          [dashboardKey]: journeys,
        },
      };
    }
    case SET_SELECTED_DATE_RANGE_AND_FREQUENCY: {
      const {chartFrequency, dashboardKey, ...dateRange} = action.payload;
      return {
        ...state,
        selectedDateRange: dateRange,
        chartFrequency,
        selectedDates: {
          ...state.selectedDates,
          [dashboardKey]: dateRange,
        },
      };
    }
    case SET_SELECTED_METRICS_TYPE: {
      return {...state, selectedMetricsType: action.payload};
    }
    case SET_CHART_FREQUENCY: {
      return {...state, chartFrequency: action.payload};
    }
    case SET_CURRENT_JOURNEY_DATA: {
      return {
        ...state,
        currentJourneyData: {
          ...state.currentJourneyData,
          ...action.payload,
        },
      };
    }

    case RECEIVE_FILTERS_OPTIONS: {
      const {filtersOptions} = state;
      return {
        ...state,
        filtersOptions: {
          ...filtersOptions,
          ...action.payload,
        },
        selectedMetricsType: 'overview',
      };
    }
    case RECEIVE_JOURNEY_OPTIONS: {
      const {filtersOptions} = state;
      return {
        ...state,
        filtersOptions: {
          ...filtersOptions,
          journey_name: action.payload,
        },
      };
    }
    case RECEIVE_JOURNEY_CLICK_INSIGHTS_TABLE_COLUMNS: {
      return {...state, journeysClickInsightsColumnFilter: action.payload};
    }
    case RECEIVE_TOUCHPOINT_TABLE_COLUMNS: {
      return {...state, touchpointColumnFilter: action.payload};
    }
    case RECEIVE_PEOPLE_TABLE_COLUMNS: {
      return {...state, peopleColumnFilter: action.payload};
    }
    case CLEAR_SELECTED_FILTERS: {
      const {dashboardKey} = action.payload;
      return {
        ...state,
        selectedFilters: {
          ...state.selectedFilters,
          [dashboardKey]: {
            journey_lifecycle_goals: [],
            journey_business_goals: [],
            journey_categories: [],
            entity_data_filter: [],
            journey_status: [],
            recipient_type: [],
            journey_ids: [],
            event_types: [],
          },
        },
        selectedJourneys: {
          ...state.selectedJourneys,
          [dashboardKey]: [],
        },
        selectedEntities: {
          ...state.selectedEntities,
          [dashboardKey]: [{id: new Date().getTime()}],
        },
        selectedDates: {
          ...state.selectedDates,
          [dashboardKey]: {
            startDateUTC: null,
            startDate: null,
            endDateUTC: null,
            endDate: null,
          },
        },
      };
    }
    case SET_CURRENT_DASHBOARD: {
      const {currentDashboard, dashboardKey} = action.payload ?? {};
      const {filters} = currentDashboard ?? {};
      const {journey_ids = []}: Filters = filters ?? {};
      return {
        ...state,
        currentDashboard: {
          ...state.currentDashboard,
          [dashboardKey]: currentDashboard,
        },
        selectedJourneys: {
          ...state.selectedJourneys,
          [dashboardKey]: journey_ids,
        },
      };
    }
    case RECEIVE_SAVED_DASHBOARDS: {
      const {filters, dashboard} = action.payload;
      return {
        ...state,
        saveDashboardFilterList: {
          ...state.saveDashboardFilterList,
          [dashboard]: filters,
        },
      };
    }
    case SET_NAVIGATION_DOWN: {
      return {
        ...state,
        isNavigationDown: action.payload,
      };
    }
    case SET_NAVIGATION_UP: {
      return {
        ...state,
        isNavigationUp: action.payload,
      };
    }

    case SET_SELECTED_DATES: {
      const {dashboardKey, dates} = action.payload;
      return {
        ...state,
        selectedDates: {
          ...state.selectedDates,
          [dashboardKey]: dates,
        },
      };
    }
    case SET_LOADING_DASHBOARD_FILTERS: {
      return {...state, isDashboardFiltersLoading: action.payload};
    }
  }

  return state;
};
